/**
 * 12-month breakdown grid for a single vendor. Extracted from Vendors.jsx
 * (Feb 2026 refactor). Fully self-contained: receives `data` (months + totals)
 * and event callbacks. Renders:
 *   - one-line summary + optional sparkline (>= 2 months with invoices)
 *   - 12-cell grid; clicking an active month opens its invoice list below
 *   - "+ Ene", "+ Feb"… button on empty months (add-invoice CTA)
 */
import { useState } from "react";
import { Trash2 } from "lucide-react";
import { formatMoney, MONTH_LABELS_ES, MONTH_LABELS_EN } from "./utils";
import { formatDateShort } from "@/lib/format";

export function VendorMonthsBreakdown({
  data, baseCurrency, lang, vendor, onAddInvoice, onDeleteInvoice, onEditInvoice, isMonthlyExpected = false,
}) {
  const labels = lang === "es" ? MONTH_LABELS_ES : MONTH_LABELS_EN;
  const months = data.months || [];
  const totals = data.totals || { subtotal: 0, tax: 0, amount: 0, invoice_count: 0 };
  const incomeByMonth = data.income_by_month || {};
  const incomeSubtotalYear = Number(data.income_subtotal_year || 0);
  const [openMonth, setOpenMonth] = useState(null);
  const monthsWithInvoices = months.filter((m) => m.invoice_count > 0).length;
  // Iter 82.7i — the expanded breakdown shows BOTH the taxable base
  // (sin IVA) and the total (con IVA) so restaurateurs can pick whichever
  // matches the report they are checking. Sparkline follows subtotal (that
  // is what hits the P&L).
  const maxAmount = Math.max(...months.map((m) => m.subtotal), 1);
  const activeMonth = openMonth ? months.find((m) => m.month_num === openMonth) : null;

  // Iter 82.7j — cost ratio per month = vendor.subtotal / income_subtotal.
  // Returns null when there is no income that month (avoids ∞).
  const pctOfIncome = (vendorSubtotal, monthNum) => {
    const inc = Number(incomeByMonth[monthNum] || 0);
    if (!inc || !vendorSubtotal) return null;
    return (vendorSubtotal / inc) * 100;
  };
  const yearPct = incomeSubtotalYear > 0
    ? (totals.subtotal / incomeSubtotalYear) * 100
    : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-[var(--text-muted)]">
          {lang === "es"
            ? `Desglose mensual ${data.year} · ${totals.invoice_count} factura(s) · Base ${formatMoney(totals.subtotal, baseCurrency)} (sin IVA) · Total ${formatMoney(totals.amount, baseCurrency)} (con IVA)`
            : `Monthly breakdown ${data.year} · ${totals.invoice_count} invoice(s) · Net ${formatMoney(totals.subtotal, baseCurrency)} (excl. VAT) · Total ${formatMoney(totals.amount, baseCurrency)} (incl. VAT)`}
          {yearPct !== null && (
            <span className="ml-1 font-semibold text-[var(--brand-accent)]" data-testid="vendor-cost-ratio-year">
              {" · "}
              {lang === "es"
                ? `${yearPct.toFixed(1)}% de la facturación anual`
                : `${yearPct.toFixed(1)}% of yearly income`}
            </span>
          )}
        </p>
        {monthsWithInvoices >= 2 && (
          <div className="flex items-center gap-2" data-testid="vendor-sparkline">
            <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              {lang === "es" ? "Tendencia" : "Trend"}
            </span>
            <svg width="120" height="22" viewBox="0 0 120 22" className="overflow-visible">
              {(() => {
                const w = 120;
                const h = 22;
                const stepX = w / Math.max(months.length - 1, 1);
                const pts = months.map((m, i) => {
                  const x = i * stepX;
                  const y = h - 2 - (m.subtotal / maxAmount) * (h - 4);
                  return [x, y, m.invoice_count > 0];
                });
                const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
                return (
                  <>
                    <path d={path} fill="none" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    {pts.map(([x, y, has], i) => has && (
                      <circle key={`pt-${i}-${x}-${y}`} cx={x} cy={y} r="1.6" fill="#16a34a" />
                    ))}
                  </>
                );
              })()}
            </svg>
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-1.5" data-testid="vendor-months-grid">
        {months.map((m) => {
          const has = m.invoice_count > 0;
          const isOpen = openMonth === m.month_num;
          return (
            <div
              key={m.month}
              data-testid={`vendor-month-${m.month_num}`}
              onClick={() => { if (has) setOpenMonth(isOpen ? null : m.month_num); }}
              className={`relative group rounded-md px-2 py-1.5 text-center border transition ${
                has
                  ? `bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60 cursor-pointer hover:ring-2 hover:ring-emerald-400/50 ${isOpen ? "ring-2 ring-emerald-500" : ""}`
                  : isMonthlyExpected
                    ? "bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800/60"
                    : "bg-[var(--surface)] border-[var(--border-color)]"
              }`}
              title={has
                ? (lang === "es" ? "Clic para ver las facturas del mes" : "Click to view month invoices")
                : isMonthlyExpected
                  ? (lang === "es" ? "Falta la factura mensual esperada" : "Expected monthly invoice missing")
                  : (lang === "es" ? "Sin facturas" : "No invoices")}
            >
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{labels[m.month_num - 1]}</div>
              <div className={`font-mono-num text-xs font-semibold ${
                has
                  ? "text-[var(--text-primary)]"
                  : isMonthlyExpected
                    ? "text-red-600 dark:text-red-400"
                    : "text-[var(--text-muted)]"
              }`}>
                {has ? formatMoney(m.subtotal, baseCurrency) : (isMonthlyExpected ? (lang === "es" ? "FALTA" : "MISSING") : "—")}
              </div>
              {has && (
                <>
                  <div className="text-[10px] font-mono-num text-[var(--text-secondary)]" title={lang === "es" ? "Total con IVA" : "Total incl. VAT"}>
                    {formatMoney(m.amount, baseCurrency)}
                    <span className="ml-0.5 text-[9px] text-[var(--text-muted)]">
                      {lang === "es" ? "c/IVA" : "w/VAT"}
                    </span>
                  </div>
                  {(() => {
                    const pct = pctOfIncome(m.subtotal, m.month_num);
                    if (pct === null) return null;
                    return (
                      <div
                        className="text-[10px] font-mono-num font-semibold text-[var(--brand-accent)]"
                        data-testid={`vendor-month-pct-${m.month_num}`}
                        title={lang === "es"
                          ? `Coste del proveedor ÷ facturación de ${labels[m.month_num - 1]} (ambos sin IVA)`
                          : `Vendor cost ÷ income of ${labels[m.month_num - 1]} (both excl. VAT)`}
                      >
                        {pct.toFixed(1)}%
                      </div>
                    );
                  })()}
                  <div className="text-[10px] text-[var(--text-muted)]">{m.invoice_count}×</div>
                  {/* iter 82.7t — "+" visible en hover para meses CON facturas,
                     por si se quiere añadir otra sin tener que vaciar el mes. */}
                  {onAddInvoice && !vendor?._ghost && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onAddInvoice(vendor, m); }}
                      data-testid={`vendor-month-add-extra-${m.month_num}`}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center bg-[var(--primary)] text-white text-[10px] font-bold shadow transition-opacity hover:scale-110"
                      title={lang === "es"
                        ? `Añadir otra factura para ${labels[m.month_num - 1]} ${data.year}`
                        : `Add another invoice for ${labels[m.month_num - 1]} ${data.year}`}
                    >
                      +
                    </button>
                  )}
                </>
              )}
              {!has && onAddInvoice && !vendor?._ghost && (
                <button
                  onClick={(e) => { e.stopPropagation(); onAddInvoice(vendor, m); }}
                  data-testid={`vendor-month-add-${m.month_num}`}
                  className="absolute inset-0 rounded-md opacity-0 group-hover:opacity-100 flex items-center justify-center bg-[var(--surface)] hover:bg-[var(--primary)]/10 text-[var(--primary)] text-[10px] font-medium border-2 border-dashed border-[var(--primary)]/40 transition-opacity"
                  title={lang === "es" ? `Añadir factura para ${labels[m.month_num - 1]} ${data.year}` : `Add invoice for ${labels[m.month_num - 1]} ${data.year}`}
                >
                  + {labels[m.month_num - 1]}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {activeMonth && activeMonth.invoices && activeMonth.invoices.length > 0 && (
        <div className="mt-2 rounded-md border border-[var(--border-color)] bg-[var(--surface)] p-3" data-testid={`vendor-month-invoices-${activeMonth.month_num}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[var(--text-secondary)]">
              {labels[activeMonth.month_num - 1]} {data.year} — {activeMonth.invoice_count} {lang === "es" ? "factura(s)" : "invoice(s)"}
            </p>
            <div className="flex items-center gap-2">
              {onAddInvoice && !vendor?._ghost && (
                <button
                  type="button"
                  onClick={() => onAddInvoice(vendor, activeMonth)}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-[var(--primary)]/40 text-[var(--primary)] hover:bg-[var(--primary)]/10"
                  data-testid={`vendor-month-add-another-${activeMonth.month_num}`}
                  title={lang === "es"
                    ? `Añadir otra factura para ${labels[activeMonth.month_num - 1]} ${data.year}`
                    : `Add another invoice for ${labels[activeMonth.month_num - 1]} ${data.year}`}
                >
                  <span className="text-sm leading-none">+</span>
                  {lang === "es" ? "Añadir factura" : "Add invoice"}
                </button>
              )}
              <button
                onClick={() => setOpenMonth(null)}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)] text-xs"
                data-testid="vendor-month-invoices-close"
              >
                ✕
              </button>
            </div>
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
              <tr className="border-b border-[var(--border-color)]">
                <th className="text-left py-1.5 px-2">{lang === "es" ? "Fecha" : "Date"}</th>
                <th className="text-left py-1.5 px-2">{lang === "es" ? "Nº factura" : "Invoice #"}</th>
                <th className="text-right py-1.5 px-2">{lang === "es" ? "Base (sin IVA)" : "Net (excl. VAT)"}</th>
                <th className="text-right py-1.5 px-2">{lang === "es" ? "Total (con IVA)" : "Total (incl. VAT)"}</th>
                <th className="text-right py-1.5 px-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {activeMonth.invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b border-[var(--border-color)] last:border-0 hover:bg-[var(--surface-hover)] cursor-pointer"
                  data-testid={`vendor-month-invoice-${inv.id}`}
                  onClick={(e) => {
                    // Ignore clicks on the delete button (has its own handler).
                    if (e.target.closest("button, a")) return;
                    // iter 82.7s — preferimos abrir el modal in-place para no
                    // salir de la página (y así no colapsar el expandido del
                    // proveedor). Si el padre no ha pasado el callback caemos
                    // en el deeplink clásico.
                    if (onEditInvoice) {
                      onEditInvoice(inv);
                    } else {
                      window.location.href = `/invoices?focus=${inv.id}`;
                    }
                  }}
                  title={lang === "es" ? "Abrir factura" : "Open invoice"}
                >
                  <td className="py-1.5 px-2 font-mono-num">{formatDateShort(inv.date)}</td>
                  <td className="py-1.5 px-2 font-mono">{inv.invoice_number || "—"}</td>
                  <td className="py-1.5 px-2 text-right font-mono-num">{formatMoney(inv.subtotal, inv.currency || baseCurrency)}</td>
                  <td className="py-1.5 px-2 text-right font-mono-num font-semibold">{formatMoney(inv.amount, inv.currency || baseCurrency)}</td>
                  <td className="py-1.5 px-2 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {onEditInvoice ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onEditInvoice(inv); }}
                          className="text-[var(--primary)] hover:underline text-[10px] px-1"
                          title={lang === "es" ? "Abrir factura" : "Open invoice"}
                          data-testid={`vendor-month-edit-${inv.id}`}
                        >
                          →
                        </button>
                      ) : (
                        <a
                          href={`/invoices?focus=${inv.id}`}
                          className="text-[var(--primary)] hover:underline text-[10px] px-1"
                          title={lang === "es" ? "Abrir factura" : "Open invoice"}
                        >
                          →
                        </a>
                      )}
                      {onDeleteInvoice && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDeleteInvoice(inv); }}
                          className="text-[var(--text-muted)] hover:text-[var(--status-negative)] p-1 rounded"
                          title={lang === "es" ? "Eliminar factura" : "Delete invoice"}
                          data-testid={`vendor-month-delete-${inv.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
